import { useState, useEffect } from 'react';
import { element, arrayOf } from 'prop-types';
import { StatusIcon } from '@podiumhq/podium-ui';
import { redirectClient, getIdToken, getUserUid } from '@podiumhq/foe-hammer';
import { useQuery, useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import get from 'lodash/get';
import { useNavigate } from 'react-router-dom';

import { GET_USER_BY_UID, useFetchCurrentUser } from '../../api/users/queries';
import {
  GET_DEFAULT_PERMISSIONS,
  ROLE_PERMISSIONS,
} from '../../api/permissions/queries';

import * as paths from '../../config/paths';

import { createRoute } from '../../lib/router';

import NoAccess from './NoAccess';
import env from '../../lib/env';

const Loading = styled.div`
  width: 100px;
  height: 100px;
  position: fixed;
  top: 50%;
  left: 50%;
  margin-top: -50px;
  margin-left: -50px;
`;

Authorization.propTypes = {
  children: arrayOf(element),
};

function Authorization(props) {
  const navigate = useNavigate();
  const [
    fetchUser,
    { data: getUserData, loading: getUserDataLoading, error: getUserDataError },
  ] = useLazyQuery(GET_USER_BY_UID);
  const [
    fetchDefaultPermissions,
    {
      data: fetchDefaultPermissionsData,
      loading: fetchDefaultPermissionsLoading,
    },
  ] = useLazyQuery(GET_DEFAULT_PERMISSIONS);
  const { currentUser, loadingCurrentUser: currentUserLoading } =
    useFetchCurrentUser();
  const { data: rolePermissionsData, loading: rolePermissionsDataLoading } =
    useQuery(ROLE_PERMISSIONS);

  const [accountsUrl] = useState(env.REACT_APP_ACCOUNT_URL || '');
  const [callbackUrl] = useState(env.REACT_APP_URL || window.location.href);
  const [loginCallbackUrl] = useState(env.REACT_APP_MITHRANDIR_URL || '');
  const [canUseAdminApp, setCanUseAdminApp] = useState(false);
  const [canViewPartner, setCanViewPartner] = useState(false);

  const currentUserUid = get(getUserData, 'user.uid', '');
  const permissions = get(
    fetchDefaultPermissionsData,
    'defaultPermissions',
    []
  );
  const { partnerUid } = currentUser;
  const userRole = get(rolePermissionsData, 'rolePermission[0].role', '');

  const { children } = props;

  // Make sure user is authorized
  useEffect(
    () => {
      async function checkAuth() {
        try {
          const token = await getIdToken(accountsUrl, true);
          const userUid = await getUserUid(token);
          fetchUser({ variables: { uid: userUid } });
        } catch {
          redirectClient(callbackUrl, accountsUrl);
        }
      }
      checkAuth();
    }, // Fix this the next time the file is edited
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      if (getUserDataError) redirectClient(loginCallbackUrl, accountsUrl);
    }, // Fix this the next time the file is edited
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getUserData, getUserDataError]
  );

  // Fetch permissions
  useEffect(
    () => {
      if (currentUserUid) {
        fetchDefaultPermissions();
      }
    }, // Fix this the next time the file is edited
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserUid]
  );

  // Redirect if user is a partner user
  useEffect(
    () => {
      const validPermissions = permissions.every(({ slug, authorized }) => {
        if (slug === 'skyhook_can_use_app') {
          return authorized;
        }
        return true;
      });
      const canViewPartner = permissions.every(({ slug, authorized }) => {
        if (slug === 'skyhook_can_view_partner') {
          return authorized;
        }
        return true;
      });

      if (validPermissions) {
        if (canViewPartner && userRole !== 'supportReps' && partnerUid) {
          navigate(createRoute(paths.PARTNER_ORGANIZATIONS, { partnerUid }));
        } else {
          setCanUseAdminApp(validPermissions);
          setCanViewPartner(canViewPartner);
        }
      } else {
        redirectClient(callbackUrl, accountsUrl);
      }
    }, // Fix this the next time the file is edited
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partnerUid, currentUser, permissions]
  );

  if (canUseAdminApp || canViewPartner) {
    return children;
  } else if (
    currentUserLoading ||
    fetchDefaultPermissionsLoading ||
    getUserDataLoading ||
    rolePermissionsDataLoading
  ) {
    return (
      <Loading data-testid="loader">
        <StatusIcon />
      </Loading>
    );
  } else {
    return <NoAccess />;
  }
}

export default Authorization;
